import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Panel } from 'primereact/panel';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Query } from 'react-apollo';
import DownloadReportButton from '../../../components/button/DownloadReportButton';
import { Gr8piFormattedDate } from '../../../components/Gr8piFormattedDateTime';
import { intlShape } from '../../../components/prop-types';
import { SURVEYS_QUERY } from '../../../graphql/SurveysQuery';
import { Loading } from '../../../components/Loading';

const i18n = defineMessages({
  C360: {
    id: 'report.360',
    defaultMessage: '360 Report',
  },
  ComparativeReport: {
    id: 'comparative.report',
    defaultMessage: 'Comparative Report',
  },
  SurveyForComparison: {
    id: 'surveylist.placeholder',
    defaultMessage: 'Select survey for comparison',
  },
  ReportTypeForComparison: {
    id: 'reportTypeList.placeholder',
    defaultMessage: 'Select report type',
  },
});

function parseUiPreferences(ownerUiPreferences) {
  try {
    const parsedProperties = JSON.parse(ownerUiPreferences);
    return parsedProperties['prefer-printer-friendly-reports']
      ? parsedProperties
      : { 'prefer-printer-friendly-reports': false };
  } catch (error) {
    return { 'prefer-printer-friendly-reports': false };
  }
}

class SurveyReport extends Component {
  constructor(props) {
    super(props);
    const { viewerLocale, ownerUiPreferences } = this.props;
    const printerFriendly = parseUiPreferences(ownerUiPreferences);
    this.state = {
      locale: viewerLocale,
      surveyPrinter: !printerFriendly['prefer-printer-friendly-reports'],
      selectedReportType: 'SURVEY_REPORT',
    };

    this.handleChangeState = this.handleChangeState.bind(this);
    this.changeSurveyPrinterFiendly = this.changeSurveyPrinterFiendly.bind(this);
  }

  handleChangeState(name, value) {
    this.setState({ [name]: value });
  }

  changeSurveyPrinterFiendly(event) {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.setState({ surveyPrinter: value });
  }

  render() {
    const {
      survey,
      locales,
      currentAccount,
      respondents,
      intl: { formatMessage },
    } = this.props;

    const {
      locale, surveyPrinter, selectedReportType, selectedSurveyToCompare,
    } = this.state;

    if (!survey || !locales) {
      return null;
    }
    let reportParams = {};

    reportParams = {};

    if (selectedReportType === 'SURVEY_REPORT') {
      reportParams = {
        account: currentAccount,
        survey: survey.id,
        printerFriendly: surveyPrinter,
        locale,
        observers: respondents,
      };
    } else if (selectedReportType === 'COMPARATIVE_REPORT') {
      reportParams = {
        account: currentAccount,
        firstSurvey: survey.id,
        secondSurvey: selectedSurveyToCompare,
        printerFriendly: surveyPrinter,
        locale,
      };
    }

    const reportList = [
      {
        value: 'SURVEY_REPORT',
        label: formatMessage(i18n.C360),
      },
      {
        value: 'COMPARATIVE_REPORT',
        label: formatMessage(i18n.ComparativeReport),
      },
    ];

    function selectedSurveyToCompareTemplate(option) {
      if (!option.value) {
        return option.label;
      }
      return (
        <div className="p-clearfix">
          <span><strong>{option.label}</strong></span>
          <span style={{ float: 'right' }}>
            <span>{`${option.labelTemplateTitle} - `}</span>
            <Gr8piFormattedDate dateValue={option.labelSurveyCreatedOn} />
          </span>
        </div>
      );
    }

    return (
      <Panel header={<FormattedMessage className="t-large" id="report.plural" defaultMessage="Reports" />}>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <div className="form-group">
              <label htmlFor="reportType" className="field-label">
                <FormattedMessage id="report" defaultMessage="Report" />
              </label>
              <Dropdown
                options={reportList}
                filter
                filterPlaceholder={formatMessage(i18n.ReportTypeForComparison)}
                filterBy="label,value"
                value={selectedReportType}
                placeholder={formatMessage(i18n.ReportTypeForComparison)}
                onChange={(e) => {
                  this.handleChangeState('selectedReportType', e.target.value);
                }}
              />
              {selectedReportType === 'COMPARATIVE_REPORT' && (
                <Query query={SURVEYS_QUERY} variables={{ accountId: currentAccount, first: 999 }}>
                  {({ data, loading, error }) => {
                    if (loading) {
                      return <Loading />;
                    }
                    if (error) {
                      return `Error ${error.message}`;
                    }

                    const edges = data && data.account && data.account.surveys ? data.account.surveys.edges : [];

                    const { respondent } = survey.self;

                    const surveyList = edges
                      .filter(
                        edge => edge.node.id !== survey.id
                          && edge.node.self
                          && edge.node.self.respondent.id === respondent.id,
                      )
                      .map(edge => ({
                        value: edge.node.id,
                        label: edge.node.name,
                        labelTemplateTitle: edge.node.self.template.title,
                        labelSurveyCreatedOn: edge.node.createdOn,
                      }));

                    return (
                      <Dropdown
                        options={surveyList}
                        value={selectedSurveyToCompare}
                        itemTemplate={selectedSurveyToCompareTemplate}
                        placeholder={formatMessage(i18n.SurveyForComparison)}
                        onChange={(e) => {
                          this.handleChangeState('selectedSurveyToCompare', e.target.value);
                        }}
                      />
                    );
                  }}
                </Query>
              )}
            </div>
          </div>
          <div className="p-col-12">
            <div className="form-group">
              <label htmlFor="locale" className="field-label">
                <FormattedMessage id="attrs.locale" defaultMessage="Preferred Language" />
              </label>
              <Dropdown
                options={locales}
                filter
                onChange={(loc) => {
                  this.handleChangeState('locale', loc.value);
                }}
                value={locale}
              />
            </div>
          </div>
          <div className="p-col-12">
            <Checkbox
              name="printerFriendlyReport"
              inputId="printerFriendlyReport"
              onChange={this.changeSurveyPrinterFiendly}
              checked={surveyPrinter}
            />
            <FormattedMessage
              id="preferences.prefer-printer-friendly-reports"
              defaultMessage="I prefer printer friendly reports"
            >
              {text => (
                <label htmlFor="printerFriendlyReport">
                  {' '}
                  {text}
                </label>
              )}
            </FormattedMessage>
          </div>
          <div className="p-col-12">
            <DownloadReportButton
              reportParams={reportParams}
              buttonClass="btn-sm"
              additionalClass="clearfix text-center"
              type={selectedReportType}
              isDisabled={selectedReportType === 'COMPARATIVE_REPORT' && selectedSurveyToCompare === undefined}
            />
          </div>
        </div>
      </Panel>
    );
  }
}

SurveyReport.propTypes = {
  survey: PropTypes.objectOf(PropTypes.any).isRequired,
  locales: PropTypes.arrayOf(PropTypes.any).isRequired,
  respondents: PropTypes.arrayOf(PropTypes.any).isRequired,
  currentAccount: PropTypes.string.isRequired,
  viewerLocale: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  ownerUiPreferences: PropTypes.objectOf(PropTypes.any),
};

SurveyReport.defaultProps = {
  ownerUiPreferences: {},
};

export default injectIntl(SurveyReport);
